var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var _renderRoot, _internals, _isMounted, _isAsync, _effectList, _unmountCallback, _execEffect, _initEffect, _render, _shouldUpdate, _updateCallback, _update, _updated, _connectedCallback;
import { html, render } from 'lit-html';
import { connect, disconnect } from './store';
import { LinkedList, addMicrotask, SheetToken, isArrayChange, GemError, kebabToCamelCase, PropProxyMap, } from './utils';
export { html, svg, render, directive, TemplateResult, SVGTemplateResult } from 'lit-html';
export { repeat } from 'lit-html/directives/repeat';
// https://github.com/Polymer/lit-html/issues/1048
export { guard } from 'lit-html/directives/guard';
export { ifDefined } from 'lit-html/directives/if-defined';
function emptyFunction() {
    // 用于占位的空函数
}
function execCallback(fun) {
    typeof fun === 'function' && fun();
}
// global render task pool
const asyncRenderTaskList = new LinkedList();
const tick = () => {
    window.requestAnimationFrame(function callback(timestamp) {
        if (performance.now() > timestamp + 16) {
            tick();
            return;
        }
        const task = asyncRenderTaskList.get();
        if (task) {
            task();
            callback(timestamp);
        }
    });
};
asyncRenderTaskList.addEventListener('start', tick);
const initSymbol = Symbol('init');
const updateSymbol = Symbol('update');
export class GemElement extends HTMLElement {
    constructor(options) {
        super();
        _renderRoot.set(this, void 0);
        _internals.set(this, void 0);
        _isMounted.set(this, void 0);
        _isAsync.set(this, void 0);
        _effectList.set(this, void 0);
        _unmountCallback.set(this, void 0);
        /**
         * @helper
         * 设置元素 state，会触发更新
         *
         * ```js
         * class App extends GemElement {
         *   click() {
         *     this.setState({});
         *   }
         * }
         * ```
         * */
        this.setState = (payload) => {
            if (!this.state)
                throw new GemError('`state` not initialized');
            Object.assign(this.state, payload);
            addMicrotask(__classPrivateFieldGet(this, _update));
        };
        /**
         * 每次更新完检查依赖，执行对应的副作用回调
         * */
        _execEffect.set(this, () => {
            var _a;
            (_a = __classPrivateFieldGet(this, _effectList)) === null || _a === void 0 ? void 0 : _a.forEach((effectItem) => {
                const { callback, getDep, values, preCallback } = effectItem;
                const newValues = getDep === null || getDep === void 0 ? void 0 : getDep();
                if (!getDep || isArrayChange(values, newValues)) {
                    execCallback(preCallback);
                    effectItem.preCallback = callback(newValues, values);
                    effectItem.values = newValues;
                }
            });
        });
        /**
         * @helper
         * 记录副作用回调和值，在 `constructor`/`mounted` 中使用
         * 回调到返回值如果是函数将再卸载时执行
         *
         * ```js
         * class App extends GemElement {
         *   mounted() {
         *     this.effect(callback, () => [this.attrName]);
         *   }
         * }
         * ```
         * */
        this.effect = (callback, getDep) => {
            if (!__classPrivateFieldGet(this, _effectList))
                __classPrivateFieldSet(this, _effectList, []);
            const values = getDep === null || getDep === void 0 ? void 0 : getDep();
            const effectItem = {
                callback,
                getDep,
                values,
                initialized: !!__classPrivateFieldGet(this, _isMounted),
            };
            // 以挂载时立即执行副作用，未挂载时等挂载后执行
            if (__classPrivateFieldGet(this, _isMounted))
                effectItem.preCallback = callback(values);
            __classPrivateFieldGet(this, _effectList).push(effectItem);
        };
        /**
         * 元素挂载后执行还未初始化的副作用
         * */
        _initEffect.set(this, () => {
            var _a;
            (_a = __classPrivateFieldGet(this, _effectList)) === null || _a === void 0 ? void 0 : _a.forEach((effectItem) => {
                const { callback, getDep, initialized } = effectItem;
                if (!initialized) {
                    effectItem.preCallback = callback(getDep === null || getDep === void 0 ? void 0 : getDep());
                    effectItem.initialized = true;
                }
            });
        });
        _render.set(this, () => {
            if (this.render)
                return this.render();
            return __classPrivateFieldGet(this, _renderRoot) === this ? undefined : html `<slot></slot>`;
        });
        _shouldUpdate.set(this, () => {
            return this.shouldUpdate ? this.shouldUpdate() : true;
        });
        _updateCallback.set(this, () => {
            if (__classPrivateFieldGet(this, _isMounted) && __classPrivateFieldGet(this, _shouldUpdate).call(this)) {
                const temp = __classPrivateFieldGet(this, _render).call(this);
                temp !== undefined && render(temp, __classPrivateFieldGet(this, _renderRoot));
                addMicrotask(__classPrivateFieldGet(this, _updated));
                addMicrotask(__classPrivateFieldGet(this, _execEffect));
            }
        });
        _update.set(this, () => {
            if (__classPrivateFieldGet(this, _isAsync)) {
                asyncRenderTaskList.add(__classPrivateFieldGet(this, _updateCallback));
            }
            else {
                __classPrivateFieldGet(this, _updateCallback).call(this);
            }
        });
        /**@helper */
        this.update = () => {
            __classPrivateFieldGet(this, _update).call(this);
        };
        _updated.set(this, () => {
            var _a;
            (_a = this.updated) === null || _a === void 0 ? void 0 : _a.call(this);
        });
        _connectedCallback.set(this, () => {
            var _a, _b;
            (_a = this.willMount) === null || _a === void 0 ? void 0 : _a.call(this);
            const { observedStores } = this.constructor;
            observedStores === null || observedStores === void 0 ? void 0 : observedStores.forEach((store) => {
                connect(store, __classPrivateFieldGet(this, _update));
            });
            const temp = __classPrivateFieldGet(this, _render).call(this);
            temp !== undefined && render(temp, __classPrivateFieldGet(this, _renderRoot));
            __classPrivateFieldSet(this, _isMounted, true);
            __classPrivateFieldSet(this, _unmountCallback, (_b = this.mounted) === null || _b === void 0 ? void 0 : _b.call(this));
            __classPrivateFieldGet(this, _initEffect).call(this);
        });
        // 外部不可见，但允许类外面使用
        addMicrotask(() => (this[initSymbol] = false));
        this[initSymbol] = true;
        this[updateSymbol] = () => {
            if (__classPrivateFieldGet(this, _isMounted)) {
                addMicrotask(__classPrivateFieldGet(this, _update));
            }
        };
        __classPrivateFieldSet(this, _isAsync, options === null || options === void 0 ? void 0 : options.isAsync);
        __classPrivateFieldSet(this, _renderRoot, (options === null || options === void 0 ? void 0 : options.isLight) ? this : this.attachShadow({ mode: 'open' }));
        const { adoptedStyleSheets } = new.target;
        if (adoptedStyleSheets) {
            const sheets = adoptedStyleSheets.map((item) => item[SheetToken] || item);
            if (this.shadowRoot) {
                this.shadowRoot.adoptedStyleSheets = sheets;
            }
            else {
                throw new GemError('Please mount to ShadowDOM or Document');
            }
        }
    }
    get internals() {
        if (!__classPrivateFieldGet(this, _internals)) {
            const getCustomStateSet = () => {
                // https://wicg.github.io/custom-state-pseudo-class/
                const { classList } = this;
                return {
                    has: (v) => classList.contains(v),
                    add: (v) => classList.add(v),
                    delete: (v) => classList.remove(v),
                };
            };
            if (!this.attachInternals) {
                // https://bugs.webkit.org/show_bug.cgi?id=197960
                this.attachInternals = () => {
                    return { states: getCustomStateSet() };
                };
            }
            __classPrivateFieldSet(this, _internals, this.attachInternals());
            if (!__classPrivateFieldGet(this, _internals).states) {
                // https://bugzilla.mozilla.org/show_bug.cgi?id=1588763
                __classPrivateFieldGet(this, _internals).states = getCustomStateSet();
            }
        }
        return __classPrivateFieldGet(this, _internals);
    }
    /**
     * @private
     * @final
     * use `effect`
     */
    attributeChangedCallback() {
        if (__classPrivateFieldGet(this, _isMounted)) {
            addMicrotask(__classPrivateFieldGet(this, _update));
        }
    }
    /**
     * @private
     * @final
     * use `mounted`
     */
    connectedCallback() {
        if (__classPrivateFieldGet(this, _isAsync)) {
            asyncRenderTaskList.add(__classPrivateFieldGet(this, _connectedCallback));
        }
        else {
            __classPrivateFieldGet(this, _connectedCallback).call(this);
        }
    }
    /**
     * @private
     * @final
     */
    // adoptedCallback() {}
    /**
     * @private
     * @final
     * use `unmounted`
     */
    disconnectedCallback() {
        var _a, _b;
        __classPrivateFieldSet(this, _isMounted, false);
        const { observedStores } = this.constructor;
        observedStores === null || observedStores === void 0 ? void 0 : observedStores.forEach((store) => {
            disconnect(store, __classPrivateFieldGet(this, _update));
        });
        execCallback(__classPrivateFieldGet(this, _unmountCallback));
        (_a = this.unmounted) === null || _a === void 0 ? void 0 : _a.call(this);
        (_b = __classPrivateFieldGet(this, _effectList)) === null || _b === void 0 ? void 0 : _b.forEach((effectItem) => {
            execCallback(effectItem.preCallback);
        });
    }
}
_renderRoot = new WeakMap(), _internals = new WeakMap(), _isMounted = new WeakMap(), _isAsync = new WeakMap(), _effectList = new WeakMap(), _unmountCallback = new WeakMap(), _execEffect = new WeakMap(), _initEffect = new WeakMap(), _render = new WeakMap(), _shouldUpdate = new WeakMap(), _updateCallback = new WeakMap(), _update = new WeakMap(), _updated = new WeakMap(), _connectedCallback = new WeakMap();
const gemElementProxyMap = new PropProxyMap();
export function defineAttribute(target, prop, attr) {
    const { booleanAttributes, numberAttributes } = target.constructor;
    Object.defineProperty(target, prop, {
        configurable: true,
        get() {
            // fix karma test
            // 判断是否是自定义元素实例
            if (!(initSymbol in this))
                return;
            const that = this;
            // 不能从 proxy 对象中读取值
            const value = that.getAttribute(attr);
            if (booleanAttributes === null || booleanAttributes === void 0 ? void 0 : booleanAttributes.has(attr)) {
                return value === null ? false : true;
            }
            if (numberAttributes === null || numberAttributes === void 0 ? void 0 : numberAttributes.has(attr)) {
                return Number(value);
            }
            return value || '';
        },
        set(v) {
            const that = this;
            const proxy = gemElementProxyMap.get(this);
            const hasSet = proxy[prop];
            const value = that.getAttribute(attr);
            // https://github.com/whatwg/dom/issues/922
            if (this[initSymbol] && value !== null && !hasSet)
                return;
            // 字段和构造函数中都有对 attr 设置时会执行多次
            // Firefox WebConsole 中不知道为什么在构造函数中 this[initSymbol] 已经为 false
            proxy[prop] = true;
            const isBool = booleanAttributes === null || booleanAttributes === void 0 ? void 0 : booleanAttributes.has(attr);
            if (v === null || v === undefined) {
                that.removeAttribute(attr);
            }
            else if (isBool) {
                // 当 attr 存在且 !!v 为 true 时，toggleAttribute 不会造成 Attribute 改变
                that.toggleAttribute(attr, !!v);
            }
            else {
                if (value !== String(v))
                    that.setAttribute(attr, String(v));
            }
        },
    });
}
const isEventHandleSymbol = Symbol('event handle');
export function defineProperty(target, prop, event, eventOptions) {
    Object.defineProperty(target, prop, {
        configurable: true,
        get() {
            const value = gemElementProxyMap.get(this)[prop];
            if (value || !event) {
                return value;
            }
            else {
                this[prop] = emptyFunction;
                return this[prop];
            }
        },
        set(v) {
            const that = this;
            const proxy = gemElementProxyMap.get(that);
            if (v !== proxy[prop]) {
                if (event) {
                    proxy[prop] = (v === null || v === void 0 ? void 0 : v[isEventHandleSymbol]) ? v
                        : (detail, options) => {
                            const evt = new CustomEvent(event, Object.assign(Object.assign(Object.assign({}, options), eventOptions), { detail }));
                            that.dispatchEvent(evt);
                            v(detail, options);
                        };
                    proxy[prop][isEventHandleSymbol] = true;
                    // emitter 不触发元素更新
                }
                else {
                    proxy[prop] = v;
                    this[updateSymbol]();
                }
            }
        },
    });
}
export function defineRef(target, prop, ref) {
    Object.defineProperty(target, prop, {
        configurable: true,
        get() {
            const proxy = gemElementProxyMap.get(this);
            let refobject = proxy[prop];
            if (!refobject) {
                const that = this;
                const ele = that.shadowRoot || that;
                refobject = {
                    get ref() {
                        return ref;
                    },
                    get element() {
                        return ele.querySelector(`[ref=${ref}]`);
                    },
                };
                proxy[prop] = refobject;
            }
            return refobject;
        },
    });
}
export function defineCSSState(target, prop, state) {
    Object.defineProperty(target, prop, {
        configurable: true,
        get() {
            var _a;
            const that = this;
            const { states } = that.internals;
            return (_a = states.has) === null || _a === void 0 ? void 0 : _a.call(states, state);
        },
        set(v) {
            const that = this;
            const { states } = that.internals;
            if (v) {
                states.add(state);
            }
            else {
                states.delete(state);
            }
        },
    });
}
export const nativeDefineElement = customElements.define.bind(customElements);
customElements.define = (name, cls, options) => {
    if (cls.prototype instanceof GemElement) {
        const { observedAttributes, observedPropertys, defineEvents, defineCSSStates, defineRefs, } = cls;
        observedAttributes === null || observedAttributes === void 0 ? void 0 : observedAttributes.forEach((attr) => defineAttribute(cls.prototype, kebabToCamelCase(attr), attr));
        observedPropertys === null || observedPropertys === void 0 ? void 0 : observedPropertys.forEach((prop) => defineProperty(cls.prototype, prop));
        defineEvents === null || defineEvents === void 0 ? void 0 : defineEvents.forEach((event) => defineProperty(cls.prototype, kebabToCamelCase(event), event));
        defineCSSStates === null || defineCSSStates === void 0 ? void 0 : defineCSSStates.forEach((state) => defineCSSState(cls.prototype, kebabToCamelCase(state), `--${state}`));
        defineRefs === null || defineRefs === void 0 ? void 0 : defineRefs.forEach((ref) => defineRef(cls.prototype, kebabToCamelCase(ref), ref));
    }
    nativeDefineElement(name, cls, options);
};
//# sourceMappingURL=element.js.map